import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Network, StickyNote, GitBranch, Sparkles, Search } from 'lucide-react'
import { getFolderListItems, getFolderNotes } from '../../services/api'

export type FolderTab = 'journals' | 'collaboration' | 'notes' | 'connections' | 'ai-analysis' | 'prior-art'

interface FolderListTabsProps {
  projectId: string
  folderId: string
  selectedDocIds: string[]
  children: (activeTab: FolderTab) => React.ReactNode
}

const TABS: { key: FolderTab; label: string; icon: React.ElementType }[] = [
  { key: 'ai-analysis', label: 'AI Analysis', icon: Sparkles },
  { key: 'prior-art', label: 'Prior Art', icon: Search },
  { key: 'journals', label: 'Journals', icon: BookOpen },
  { key: 'collaboration', label: 'Collaboration', icon: Network },
  { key: 'notes', label: 'Notes', icon: StickyNote },
  { key: 'connections', label: 'Connections', icon: GitBranch },
]

export default function FolderListTabs({ projectId, folderId, selectedDocIds: _selectedDocIds, children }: FolderListTabsProps) {
  void _selectedDocIds // Used by parent to pass to connections graph
  const [activeTab, setActiveTab] = useState<FolderTab>('journals')

  const { data: journalsData } = useQuery({
    queryKey: ['folder-list', projectId, folderId, 'journal'],
    queryFn: () => getFolderListItems(projectId, folderId, 'journal'),
    enabled: !!projectId && !!folderId,
  })

  const { data: contactsData } = useQuery({
    queryKey: ['folder-list', projectId, folderId, 'contact'],
    queryFn: () => getFolderListItems(projectId, folderId, 'contact'),
    enabled: !!projectId && !!folderId,
  })

  const { data: orgsData } = useQuery({
    queryKey: ['folder-list', projectId, folderId, 'organisation'],
    queryFn: () => getFolderListItems(projectId, folderId, 'organisation'),
    enabled: !!projectId && !!folderId,
  })

  const { data: notesData } = useQuery({
    queryKey: ['folder-notes', projectId, folderId],
    queryFn: () => getFolderNotes(projectId, folderId),
    enabled: !!projectId && !!folderId,
  })

  const counts: Record<FolderTab, number> = {
    journals: journalsData?.count ?? 0,
    collaboration: (contactsData?.count ?? 0) + (orgsData?.count ?? 0),
    notes: notesData?.count ?? 0,
    connections: 0,
    'ai-analysis': 0,
    'prior-art': 0,
  }

  return (
    <div className="mt-4 light-card">
      <div className="flex flex-wrap border-b border-sky-200/50">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === key
                ? 'border-iflow-500 text-iflow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {counts[key] > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === key ? 'bg-iflow-100 text-iflow-700' : 'bg-sky-500/10 text-gray-600'
              }`}>
                {counts[key]}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="p-4">
        {children(activeTab)}
      </div>
    </div>
  )
}
